import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { downloadFile } from '../api';

interface Answer { text: string; rating: number | null; who: string | null; layer: string; department: string | null; }
interface Q { id: number; text: string; type: string; scale: number; count: number; average: number | null; distribution: Record<string, number>; option_counts: Record<string, number>; answers: Answer[]; }
interface Results { template: { id: number; name: string; cycle_id: number; description: string | null }; total: number; submitted: number; by_department: { department: string; total: number; submitted: number }[]; questions: Q[]; named: boolean; }

/** Aggregated results of an organisation-wide questionnaire (anonymous by default; names on request). */
export default function AdminSurvey() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [res, setRes] = useState<Results | null>(null);
  const [named, setNamed] = useState(false);
  const [filterDept, setFilterDept] = useState('');

  useEffect(() => {
    api.get(`/exports/survey/${templateId}?named=${named ? 1 : 0}`).then(({ data }) => setRes(data)).catch(() => navigate('/admin/cycles'));
  }, [templateId, named, navigate]);

  if (!res) return <div className="loading"><div className="spinner" />טוען תוצאות...</div>;
  const pct = res.total ? Math.round(res.submitted / res.total * 100) : 0;
  const depts = res.by_department.map(d => d.department);

  return (
    <div className="page">
      <div className="page-header">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/cycles/${res.template.cycle_id}/questionnaires`)}>← חזור לשאלונים</button>
        <h1 style={{ marginTop: '12px' }}>תוצאות: {res.template.name}</h1>
        <div className="text-muted text-sm">{res.submitted} מתוך {res.total} הגישו ({pct}%)</div>
        <div className="page-header-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-input" style={{ width: 'auto' }} value={filterDept} onChange={e => setFilterDept(e.target.value)}>
            <option value="">כל המחלקות</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <label className="checklist-item" style={{ margin: 0 }}>
            <input type="checkbox" checked={named} onChange={e => setNamed(e.target.checked)} />
            <span className="text-sm">הצג שמות משיבים (לעיונך בלבד)</span>
          </label>
          <button className="btn btn-primary" onClick={() => downloadFile(`/exports/excel/${res.template.cycle_id}`, `כל_התשובות.xlsx`)}>⬇ Excel של כל התשובות</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><div className="card-title">מילוי לפי מחלקה</div></div>
        {res.by_department.map(d => (
          <div key={d.department} style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}><span>{d.department}</span><span className="text-muted">{d.submitted}/{d.total}</span></div>
            <div className="progress-bar"><div className="progress-bar-fill" style={{ width: `${d.total ? d.submitted / d.total * 100 : 0}%` }} /></div>
          </div>
        ))}
      </div>

      {res.questions.map((q, i) => {
        const answers = q.answers.filter(a => !filterDept || a.department === filterDept);
        return (
          <div key={q.id} className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header"><div className="card-title" style={{ whiteSpace: 'pre-wrap' }}>{i + 1}. {q.text}</div><div className="text-muted text-sm">{q.count} תשובות</div></div>
            {q.average !== null && (
              <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div className="stat-card" style={{ padding: '12px 20px' }}><div className="stat-number" style={{ fontSize: '28px' }}>{q.average}</div><div className="stat-label">ממוצע מתוך {q.scale}</div></div>
                <div style={{ flex: 1, minWidth: '220px' }}>
                  {Array.from({ length: q.scale }, (_, k) => q.scale - k).map(v => {
                    const n = q.distribution[String(v)] || 0; const w = q.count ? n / q.count * 100 : 0;
                    return (
                      <div key={v} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '3px' }}>
                        <span style={{ width: '18px', textAlign: 'center', fontWeight: 600 }}>{v}</span>
                        <div className="progress-bar" style={{ flex: 1 }}><div className="progress-bar-fill" style={{ width: `${w}%` }} /></div>
                        <span className="text-muted" style={{ width: '32px' }}>{n}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {Object.keys(q.option_counts).length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                {Object.entries(q.option_counts).map(([opt, n]) => (
                  <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '4px' }}>
                    <span style={{ flex: 1 }}>{opt}</span>
                    <div className="progress-bar" style={{ width: '160px' }}><div className="progress-bar-fill" style={{ width: `${q.count ? n / q.count * 100 : 0}%` }} /></div>
                    <span className="text-muted" style={{ width: '32px' }}>{n}</span>
                  </div>
                ))}
              </div>
            )}
            {answers.length > 0 && (
              <div>
                {answers.map((a, k) => (
                  <div key={k} style={{ padding: '8px 12px', borderRight: '3px solid var(--cyan-100)', background: 'var(--cyan-50)', marginBottom: '6px', borderRadius: '4px', whiteSpace: 'pre-wrap' }}>
                    {a.text}
                    <div className="text-muted text-sm" style={{ marginTop: '4px' }}>{a.who ? `${a.who} · ` : ''}{a.layer}{a.department ? ` · ${a.department}` : ''}{a.rating !== null ? ` · ציון ${a.rating}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
